import {APIService} from "../../common/APIService";
import {Relation, CustomerRelationType} from "../../dto/Relation";
import {ProjectSettings} from "../../dto/ProjectSettings";
import {RelationNote} from "../../dto/RelationNote";

export class RelationService extends APIService {
  public async browse() {
    const json = await this.getJson<{data: object[]}>(`relations`)
    return json.data.map(item => Relation.fromJson(item))
  }

  public async add(
    name: string,
    type: CustomerRelationType,
    alias: string,
    phone: string,
    locationStreet: string,
    locationHouseNumber: string,
    locationHouseNumberAddition: string,
    city: string,
    postalcode: string,
    web: string,
    email: string,
    paymentTerm: number,
    vatRateId: string|null,
    cocNumber: string|null,
    vatNumber: string|null,
    iban: string|null,
    isSupplier: boolean,
    defaultLedgerAccount: string|null,
  ) {
    const json = await this.putJson<{data: object[]}>(`relations`, {
      name: name,
      type: type,
      alias: alias,
      phone: phone,
      location_address_streetname: locationStreet,
      location_address_house_number: locationHouseNumber,
      location_address_house_number_addition: locationHouseNumberAddition,
      location_city: city,
      location_zipcode: postalcode,
      web: web,
      email: email,
      payment_term: paymentTerm,
      vat_rate_id: vatRateId,
      coc_number: cocNumber,
      vat_number: vatNumber,
      iban: iban,
      is_supplier: isSupplier,
      supplier_default_ledger_account_id: defaultLedgerAccount,
    })
    return Relation.fromJson(json.data)
  }

  public async addFromProject(
    name: string,
    type: CustomerRelationType,
    phone: string,
    email: string,
    locationStreet: string,
    locationHouseNumber: string,
    locationHouseNumberAddition: string,
    city: string,
    postalcode: string,
    isSupplier: boolean,
    projectId: string,
    paymentTerm: number|null,
    vatRateId: string|null,
  ) {
    const json = await this.putJson<{data: object[]}>(`relations`, {
      name: name,
      type: type,
      phone: phone,
      location_address_streetname: locationStreet,
      location_address_house_number: locationHouseNumber,
      location_address_house_number_addition: locationHouseNumberAddition,
      location_city: city,
      location_zipcode: postalcode,
      email: email,
      is_supplier: isSupplier,
      project_id: projectId,
      payment_term: paymentTerm,
      vat_rate_id: vatRateId,
    })
    return Relation.fromJson(json.data)
  }

  public async edit(
    relationId: string,
    name: string|null,
    type: CustomerRelationType|null,
    alias: string|null,
    phone: string|null,
    locationStreet: string|null,
    locationHouseNumber: string|null,
    locationHouseNumberAddition: string|null,
    city: string|null,
    postalcode: string|null,
    web: string|null,
    email: string|null,
    paymentTerm: number|null,
    vatRateId: string|null,
    projectSettings?: ProjectSettings,
    cocNumber?: string|null,
    vatNumber?: string|null,
    iban?: string|null,
    isSupplier?: boolean,
    defaultLedgerAccount?: string|null,
  ) {
    const json = await this.putJson<{data: object[]}>(`relations/${relationId}`, {
      name: name,
      type: type,
      alias: alias,
      phone: phone,
      location_address_streetname: locationStreet,
      location_address_house_number: locationHouseNumber,
      location_address_house_number_addition: locationHouseNumberAddition,
      location_city: city,
      location_zipcode: postalcode,
      web: web,
      email: email,
      payment_term: paymentTerm,
      vat_rate_id: vatRateId,
      ...(projectSettings !== undefined ? {
        'should_request_purchase_order': projectSettings.shouldRequestPurchaseOrder,
        'should_send_man_day_sheet': projectSettings.shouldSendManDaySheet,
        'should_use_g_account': projectSettings.shouldUseGAccount,
        'requires_project_number': projectSettings.requiresProjectNumber,
      } : {}),
      ...(cocNumber !== undefined ? {'coc_number': cocNumber} : {}),
      ...(vatNumber !== undefined ? {'vat_number': vatNumber} : {}),
      ...(iban !== undefined ? {'iban': iban} : {}),
      ...(isSupplier !== undefined ? {'is_supplier': isSupplier} : {}),
      ...(defaultLedgerAccount !== undefined ? {'supplier_default_ledger_account_id': defaultLedgerAccount} : {}),
    })
    return Relation.fromJson(json.data)
  }

  public async delete(relationId: string): Promise<void> {
    await this.deleteJson(`relations/${relationId}`)
  }

  public async getCoordinates(relationId: string): Promise<{lat: number, lng: number}> {
    const json = await this.getJson<{data: {lat: number, lng: number}}>(`relations/${relationId}/coordinates`)
    return json.data
  }

  public async addNote(relationId: string, note: string): Promise<RelationNote> {
    const json = await this.postJson<{data: object[]}>(`relations/${relationId}/notes`, {
      note: note
    })
    return RelationNote.fromJson(json.data)
  }

  public async editNote(relationId: string, relationNoteId: string, note: string): Promise<RelationNote> {
    const json = await this.putJson<{data: object[]}>(`relations/${relationId}/notes/${relationNoteId}`, {
      note: note
    })
    return RelationNote.fromJson(json.data)
  }

  public async deleteNote(relationId: string, relationNoteId: string): Promise<void> {
    await this.deleteJson(`relations/${relationId}/notes/${relationNoteId}`)
  }
}